import type {
  WidgetDataSource,
  WidgetDataSourceEntry,
  WidgetExecuteRequest,
} from "../types/widget";
import type { DateRangeValue } from "../components/DateRangeHeader";

export function normalizeWidgetSources(dataSource?: WidgetDataSource): WidgetDataSourceEntry[] {
  if (!dataSource) return [];
  if (dataSource.sources?.length) {
    return dataSource.sources
      .filter((s) => s?.index?.trim())
      .map((s) => ({
        index: s.index.trim(),
        timeField: s.timeField?.trim() || dataSource.timeField?.trim() || "",
      }));
  }
  return (dataSource.indices || [])
    .filter((index) => index?.trim())
    .map((index) => ({
      index: index.trim(),
      timeField: dataSource.timeField?.trim() || "",
    }));
}

export function mergeSourcesByIndices(
  existing: WidgetDataSourceEntry[],
  indices: string[],
  fallbackTimeField = ""
): WidgetDataSourceEntry[] {
  const byIndex = new Map(existing.map((s) => [s.index, s]));
  return indices.map((index) => {
    const previous = byIndex.get(index);
    return {
      index,
      timeField: previous?.timeField || fallbackTimeField,
    };
  });
}

export function applySourcesToDataSource(
  dataSource: WidgetDataSource | undefined,
  sources: WidgetDataSourceEntry[]
): WidgetDataSource {
  const cleaned = sources
    .filter((s) => s.index.trim())
    .map((s) => ({
      index: s.index.trim(),
      timeField: s.timeField?.trim() || undefined,
    }));
  const firstTimeField = cleaned.find((s) => s.timeField)?.timeField;
  return {
    ...dataSource,
    sources: cleaned,
    indices: cleaned.map((s) => s.index),
    timeField: firstTimeField,
  };
}

export type SourceDateRange = {
  index: string;
  from: string;
  to: string;
};

export function buildSourceDateRanges(
  sources: WidgetDataSourceEntry[],
  fallback: { from: string; to: string }
): SourceDateRange[] {
  return sources.map((source) => ({
    index: source.index,
    from: fallback.from,
    to: fallback.to,
  }));
}

export function syncSourceDateRanges(
  previous: SourceDateRange[],
  sources: WidgetDataSourceEntry[],
  fallback: { from: string; to: string }
): SourceDateRange[] {
  const byIndex = new Map(previous.map((range) => [range.index, range]));
  return sources.map((source) => {
    const existing = byIndex.get(source.index);
    return existing ?? { index: source.index, from: fallback.from, to: fallback.to };
  });
}

export function buildWidgetExecuteRequest(
  sources: WidgetDataSourceEntry[],
  sourceDateRanges: SourceDateRange[],
  globalRange: DateRangeValue,
  timeZone?: string
): WidgetExecuteRequest {
  const request: WidgetExecuteRequest = {
    from: globalRange.from,
    to: globalRange.to,
  };
  if (timeZone?.trim()) {
    request.timezone = timeZone.trim();
  }
  if (sources.length > 1) {
    request.sourceRanges = sourceDateRanges.map((range) => ({
      index: range.index,
      from: range.from,
      to: range.to,
    }));
  }
  return request;
}
